import { useState } from "react";
import { api } from "../../api/client";
import { ActionStatus, PayFeeSelect, usePayFeeIn } from "../../components/ActionStatus";
import { usePlayer } from "../../state/PlayerContext";
import { t } from "../../strings";
import { formatRcd } from "../format";
import { useWalletAction } from "../useWalletAction";

function minBig(a: string, b: string): string {
  return BigInt(a) < BigInt(b) ? a : b;
}

/** In-game RCD, wallet RCD, today's claim allowance, Claim / Deposit (§7.6 `rcd`, §7.7 flow). */
export function RcdPanel({ depositOnly = false }: { depositOnly?: boolean } = {}) {
  const { me, inSession, refresh } = usePlayer();
  const claim = useWalletAction();
  const deposit = useWalletAction();
  const [claimAmount, setClaimAmount] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const allowance = useWalletAction();
  const [allowanceAmount, setAllowanceAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<"deposit" | "claim" | "limit">("deposit");
  const [confirmClaim, setConfirmClaim] = useState(false);
  const fee = usePayFeeIn();
  const payFeeIn = fee.value;

  if (!me) return null;
  const remaining = minBig(me.claim.remainingToday, me.claim.globalRemainingToday);
  // pay_relay_fee charges the network fee plus any fee debt; a SOL-paid deposit repays the debt at the next fee instead.
  const depositAmt = Number(depositAmount);
  const relayFee = payFeeIn === "RCD" ? me.fees.perRelayedTx : 0;
  const feeDebt = Number(me.feeDebtRcd);
  const breakdown = depositAmt > 0 && (relayFee || feeDebt) ? t.depositBreakdown(relayFee, feeDebt, Math.max(0, depositAmt - relayFee - feeDebt)) : null;

  const onClaim = async () => {
    const amount = Number(claimAmount);
    if (!Number.isInteger(amount) || amount <= 0) return;
    let confirmMidSession = false;
    if (inSession || me.activeSession) {
      if (!confirmClaim) { setConfirmClaim(true); return; }
      confirmMidSession = true;
    }
    const r = await claim.run(() => api.rcdClaim(amount, confirmMidSession));
    if (r) {
      setClaimAmount("");
      setConfirmClaim(false);
      void refresh();
    }
  };

  const onAllowance = async () => {
    const amount = Number(allowanceAmount);
    if (!Number.isInteger(amount) || amount < 0) return;
    const r = await allowance.run(() => api.rcdAllowance(amount, payFeeIn));
    if (r) {
      setAllowanceAmount("");
      void refresh();
    }
  };

  const onDeposit = async () => {
    const amount = Number(depositAmount);
    if (!Number.isInteger(amount) || amount <= 0) return;
    const r = await deposit.run(() => api.rcdDeposit(amount, payFeeIn));
    if (r) {
      setDepositAmount("");
      void refresh();
    }
  };

  const selected = depositOnly ? "deposit" : tab;
  const value = selected === "deposit" ? depositAmount : selected === "claim" ? claimAmount : allowanceAmount;
  const setValue = selected === "deposit" ? setDepositAmount : selected === "claim" ? setClaimAmount : setAllowanceAmount;
  const busy = claim.busy || deposit.busy || allowance.busy;
  const valid = value !== "" && Number.isSafeInteger(Number(value)) && (selected === "limit" ? Number(value) >= 0 : Number(value) > 0);
  const available = selected === "deposit" ? me.walletRcd : selected === "claim" ? minBig(remaining, me.gameBalance) : me.walletRcd;
  const title = selected === "deposit" ? "Move money into your game" : selected === "claim" ? "Move money to your wallet" : "Set your minting allowance";
  const help = selected === "deposit" ? (depositOnly ? "Add ₽ to cover waiting mints. Save in-game after depositing." : "Your deposit credits your in-game purse.") : selected === "claim" ? "Claimed ₽ leaves your game and arrives in your wallet." : "Let the game cover minting costs from your wallet, up to this limit. Set 0 to turn it off.";
  return <section className="wallet-design" aria-label={depositOnly ? "Deposit for minting" : "Wallet"}>
    <div className="wallet-balances"><div><span>IN GAME</span><strong>{formatRcd(me.gameBalance)}</strong><small>Synced balance</small></div><div><span>IN WALLET</span><strong>{formatRcd(me.walletRcd)}</strong><small>Available to deposit</small></div></div>
    {!depositOnly && <div className="wallet-tabs" role="tablist" aria-label="Wallet action">
      {(["deposit", "claim", "limit"] as const).map(v => <button key={v} role="tab" aria-selected={selected === v} disabled={busy} onClick={() => { setTab(v); setConfirmClaim(false); }}>{v === "limit" ? "Spending limit" : v === "deposit" ? "Deposit" : "Claim"}</button>)}
    </div>}
    <div className="wallet-action-card">
      <div className="wallet-route"><span>{selected === "claim" ? "GAME" : "WALLET"}</span><span aria-hidden="true">→</span><span>{selected === "claim" ? "WALLET" : selected === "limit" ? "MINTING" : "GAME"}</span></div>
      <h3>{title}</h3><p>{help}</p>
      {selected === "limit" && <p className="wallet-mini-stat">Current limit <strong>{formatRcd(me.walletAllowance.approved)}</strong></p>}
      <label className="wallet-amount-label" htmlFor="wallet-action-amount">{selected === "limit" ? "Spending limit" : "Amount"}</label>
      <div className="wallet-amount"><span>₽</span><input id="wallet-action-amount" data-modal-autofocus inputMode="numeric" placeholder="0" aria-label={selected === "deposit" ? "Deposit amount" : selected === "claim" ? "Claim amount" : "Spending limit"} value={value} onChange={e => { setValue(e.target.value.replace(/\D/g, "")); setConfirmClaim(false); }} /><span>RCD</span></div>
      <div className="wallet-quick"><span>{selected === "claim" ? "Claimable" : "Available"} {formatRcd(available)}</span><button disabled={busy} onClick={() => { setValue((BigInt(available) / 2n).toString()); setConfirmClaim(false); }}>50%</button><button disabled={busy} onClick={() => { setValue(available); setConfirmClaim(false); }}>Max</button></div>
      {selected !== "claim" && <div className="wallet-fee"><PayFeeSelect {...fee} /></div>}
      {selected === "deposit" && breakdown && <p className="wallet-note">{breakdown}</p>}
      {selected === "claim" && confirmClaim && <p className="wallet-note">{t.midSessionClaim}</p>}
      <button className="wallet-submit" disabled={busy || !valid || (selected === "claim" && me.claim.paused)} onClick={selected === "deposit" ? onDeposit : selected === "claim" ? onClaim : onAllowance}>{busy ? "Processing…" : selected === "deposit" ? "Deposit" : selected === "claim" ? confirmClaim ? "Confirm claim" : "Claim" : "Save spending limit"} <span aria-hidden="true">↗</span></button>
      <ActionStatus {...(selected === "deposit" ? deposit : selected === "claim" ? claim : allowance)} />
    </div>
    {(BigInt(me.moneyOffset) > 0n || BigInt(me.depositAddress.pendingRcd) > 0n) && <p className="wallet-note">Arriving in game: {formatRcd(BigInt(me.moneyOffset) > 0n ? me.moneyOffset : me.depositAddress.pendingRcd)}</p>}
    {BigInt(me.feeDebtRcd) > 0n && <p className="wallet-note">{t.feeDebt(me.feeDebtRcd)}</p>}
    <details className="wallet-address"><summary>Deposit from another wallet</summary><p>Send ₽ to this address. It is credited at your next save.</p><code>{me.depositAddress.address}</code><button onClick={() => void navigator.clipboard.writeText(me.depositAddress.address).then(() => setCopied(true)).catch(() => setCopied(false))}>{copied ? "Copied" : "Copy address"}</button></details>
  </section>;
}

